import type { ReactNode } from 'react'
import type { FrameMotif, FrameTone } from '@/types'

import Image from 'next/image'
import { useId } from 'react'
import { cn } from '@/lib/utils'

interface ArtFrameProps {
    caption: string
    alt: string
    index?: string
    src?: string
    imageClassName?: string
    tone?: FrameTone
    motif?: FrameMotif
    className?: string
    sizes?: string
    priority?: boolean
    showCaption?: boolean
    showCorners?: boolean
    children?: ReactNode
}

interface MotifProps {
    motif: FrameMotif
    id: string
}

const LINE = 'rgb(237 230 216 / 0.13)'
const FAINT = 'rgb(237 230 216 / 0.07)'
const BRASS = 'rgb(230 207 163 / 0.55)'

const Motif = ({ motif, id }: MotifProps): ReactNode => {
    if (motif === 'none') return null

    const glow = `${id}-glow`

    return (
        <svg
            viewBox='0 0 100 100'
            preserveAspectRatio='xMidYMid meet'
            className='pointer-events-none absolute inset-0 h-full w-full'
            aria-hidden='true'
        >
            <defs>
                <radialGradient id={glow}>
                    <stop offset='0%' stopColor='rgb(255 226 180)' stopOpacity='0.5' />
                    <stop offset='45%' stopColor='rgb(230 184 118)' stopOpacity='0.14' />
                    <stop offset='100%' stopColor='rgb(201 171 124)' stopOpacity='0' />
                </radialGradient>
            </defs>

            {motif === 'plate' && (
                <g>
                    <ellipse cx='50' cy='56' rx='24' ry='18' fill={`url(#${glow})`} opacity='0.45' />
                    <circle cx='50' cy='54' r='30' fill='none' stroke={LINE} strokeWidth='1' vectorEffect='non-scaling-stroke' />
                    <circle cx='50' cy='54' r='21' fill='none' stroke={FAINT} strokeWidth='1' vectorEffect='non-scaling-stroke' />
                    <path d='M 25.5 37 A 30 30 0 0 1 50 24' fill='none' stroke={BRASS} strokeWidth='1' strokeLinecap='round' vectorEffect='non-scaling-stroke' />
                </g>
            )}

            {motif === 'portrait' && (
                <g>
                    <ellipse cx='50' cy='42' rx='22' ry='28' fill={`url(#${glow})`} opacity='0.5' />
                    <line x1='50' y1='0' x2='50' y2='12' stroke={FAINT} strokeWidth='1' vectorEffect='non-scaling-stroke' />
                </g>
            )}

            {motif === 'room' && (
                <g>
                    {[{ x: 28, y: 26 }, { x: 50, y: 34 }, { x: 72, y: 22 }].map(lamp => (
                        <g key={lamp.x}>
                            <line x1={lamp.x} y1='0' x2={lamp.x} y2={lamp.y - 2} stroke={LINE} strokeWidth='1' vectorEffect='non-scaling-stroke' />
                            <circle cx={lamp.x} cy={lamp.y + 2} r='11' fill={`url(#${glow})`} />
                            <path
                                d={`M ${lamp.x - 3.2} ${lamp.y + 1.2} Q ${lamp.x} ${lamp.y - 3.4} ${lamp.x + 3.2} ${lamp.y + 1.2} Z`}
                                fill='rgb(201 171 124 / 0.75)'
                            />
                        </g>
                    ))}
                    <line x1='0' y1='74' x2='100' y2='74' stroke={FAINT} strokeWidth='1' vectorEffect='non-scaling-stroke' />
                </g>
            )}

            {motif === 'flame' && (
                <g>
                    <ellipse cx='50' cy='70' rx='16' ry='24' fill={`url(#${glow})`} />
                    <ellipse cx='50' cy='76' rx='7' ry='11' fill={`url(#${glow})`} />
                    <line x1='20' y1='90' x2='80' y2='90' stroke={FAINT} strokeWidth='1' vectorEffect='non-scaling-stroke' />
                </g>
            )}

            {motif === 'bread' && (
                <g>
                    <ellipse cx='50' cy='62' rx='30' ry='14' fill={`url(#${glow})`} opacity='0.6' />
                    <path d='M 22 66 Q 50 50 78 66' fill='none' stroke={LINE} strokeWidth='1' vectorEffect='non-scaling-stroke' />
                </g>
            )}
        </svg>
    )
}

const Corners = (): ReactNode => (
    <span aria-hidden='true' className='pointer-events-none absolute inset-3 md:inset-4'>
        <span className='absolute left-0 top-0 size-3 border-l border-t border-brass/50' />
        <span className='absolute right-0 top-0 size-3 border-r border-t border-brass/50' />
        <span className='absolute bottom-0 left-0 size-3 border-b border-l border-brass/50' />
        <span className='absolute bottom-0 right-0 size-3 border-b border-r border-brass/50' />
    </span>
)

const ArtFrame = ({
    caption,
    alt,
    index,
    src,
    imageClassName,
    tone = 'ember',
    motif = 'none',
    className,
    sizes = '(min-width: 1024px) 50vw, 100vw',
    priority = false,
    showCaption = true,
    showCorners = true,
    children
}: ArtFrameProps): ReactNode => {
    const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')

    return (
        <div
            className={cn('relative isolate overflow-hidden bg-charcoal-2', !src && `frame-${tone}`, className)}
            role={src ? undefined : 'img'}
            aria-label={src ? undefined : alt}
        >
            {src ? (
                <>
                    <Image
                        src={src}
                        alt={alt}
                        fill
                        sizes={sizes}
                        priority={priority}
                        className={cn('object-cover saturate-[0.9]', imageClassName)}
                    />
                    <span aria-hidden='true' className='absolute inset-0 bg-linear-to-t from-ink/35 via-transparent to-transparent' />
                </>
            ) : (
                <Motif motif={motif} id={id} />
            )}

            {!src && <span aria-hidden='true' className='frame-vignette pointer-events-none absolute inset-0' />}

            {!src && showCorners && <Corners />}

            {!src && (index || showCaption) && (
                <span aria-hidden='true' className='pointer-events-none absolute inset-x-6 bottom-6 top-6 flex flex-col justify-between md:inset-x-8 md:bottom-7 md:top-7'>
                    <span className='caps text-[0.625rem] text-ivory/45'>
                        {index ? `No. ${index}` : ''}
                    </span>

                    {showCaption && (
                        <span className='caps max-w-[22ch] text-[0.625rem] leading-relaxed text-ivory/60'>
                            {caption}
                        </span>
                    )}
                </span>
            )}

            {children}
        </div>
    )
}

export default ArtFrame
